import axios from 'axios'

import { API_BASE } from './config'
import { registerSessionBaggage } from './sessionBaggage'

const passwordLoginClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

registerSessionBaggage(passwordLoginClient)

export interface AuthenticatedPasswordLoginResponse {
  access: string
  refresh: string
  user?: unknown
}

export interface EmailVerificationRequiredResponse {
  code?: 'email_verification_required'
  verification_token: string
  email_verified: false
  email: string
  user_id: number
  message: string
}

export type PasswordLoginResponse =
  | AuthenticatedPasswordLoginResponse
  | EmailVerificationRequiredResponse

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isAuthenticatedResponse(value: unknown): value is AuthenticatedPasswordLoginResponse {
  return isRecord(value)
    && value.email_verified !== false
    && (!isRecord(value.user) || value.user.email_verified !== false)
    && typeof value.access === 'string'
    && typeof value.refresh === 'string'
}

function isVerificationRequiredResponse(value: unknown): value is EmailVerificationRequiredResponse {
  if (!isRecord(value)) return false

  const hasKnownCode = value.code === undefined || value.code === 'email_verification_required'
  return hasKnownCode
    && value.email_verified === false
    && typeof value.verification_token === 'string'
    && typeof value.email === 'string'
    && typeof value.user_id === 'number'
    && typeof value.message === 'string'
}

export function isEmailVerificationRequired(
  response: PasswordLoginResponse,
): response is EmailVerificationRequiredResponse {
  return 'email_verified' in response && response.email_verified === false
}

export function parsePasswordLoginResponse(value: unknown): PasswordLoginResponse {
  // Verification wins if a future backend accidentally includes both shapes.
  // An unverified account must never become authenticated in the client.
  if (isVerificationRequiredResponse(value)) return value
  if (isAuthenticatedResponse(value)) return value

  throw new Error('Unexpected password login response')
}

export async function passwordLogin(email: string, password: string): Promise<PasswordLoginResponse> {
  const { data } = await passwordLoginClient.post<unknown>('/api/token/', {
    username: email,
    password,
  })
  return parsePasswordLoginResponse(data)
}
